import { type FC, createContext, useContext, useMemo, type ReactNode } from 'react';
import serverVars from 'server-vars';

import { type PageType, PAGE_TYPES } from '../pageType.ts';

const DEV_BUILD = process.env.NODE_ENV !== 'production';

// maybe roll this into usePageType?

type AvailablePageTypes = PageType[];

const AvailablePageTypesContext = createContext<AvailablePageTypes>([]);

function assertPageTypes(context: unknown): asserts context is PageType[] {
    if (DEV_BUILD) {
        if (!Array.isArray(context))
            throw Error(`AvailablePageTypesContext must be an array. Got: ${typeof context}`);
        context.forEach(item => {
            if (!(PAGE_TYPES as readonly string[]).includes(item)) {
                throw Error(`AvailablePageTypesContext includes invalid PageType value: ${item}`);
            }
        });
    }
}

export const useAvailablePageTypes = (): AvailablePageTypes =>
    useContext(AvailablePageTypesContext);

export const AvailablePageTypesProvider: FC<{
    serverPageTypes?: string[];
    children: ReactNode;
}> = ({ serverPageTypes, children }) => {
    const pageTypes = serverPageTypes || serverVars.get('availablePages');
    assertPageTypes(pageTypes);

    const availablePageTypes = useMemo(
        // filter on const to ensure order as set on PAGE_TYPES
        () => PAGE_TYPES.filter(item => pageTypes.includes(item)),
        [pageTypes]
    );

    return (
        <AvailablePageTypesContext value={availablePageTypes}>{children}</AvailablePageTypesContext>
    );
};
